import { Box } from 'devextreme-react';
import { Item } from 'devextreme-react/box';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import { getListApi } from '../../../apiUtils';
import AssistanceDataGrid from '../../../components/datagrid/AssistanceDataGrid';
import { useAssetClass } from '../../../contexts/assetClass';
import { CONFIG_KEYS, useConfigByKey } from '../../../contexts/configContext';
import { useTerminalAuth } from '../../../contexts/terminalAuthContext';
import useQueryParams from '../../../hooks/useQueryParams';
import { useSessionStorage } from '../../../hooks/useSessionStorage';
import { PageContentWrapper } from '../../../layouts/contentWrapper/PageContentWrapper';
import { CapacityCategory } from '../../../types/capacityCategory.types';
import { Task } from '../../../types/task.types';
import { API_URL, fetcher } from '../../../utils/apiUrl';
import { STORAGE_KEYS } from '../../../utils/localStorageKeys';

// ms
const DEFAULT_REFRESH_INTERVAL = 10000;

interface PageParams {
  assetId?: string;
  capacityCategoryArr?: string[];
}

export function TerminalAssistanceQueue() {
  const queryParams: PageParams = useQueryParams();
  const { user, assetIdFromUrl, terminalMappings, capacityCategoryIdArrFromUrl } =
    useTerminalAuth();
  const { assetClass } = useAssetClass();
  const refreshInterval = useConfigByKey(CONFIG_KEYS.TERMINAL_ASSISTANCE_QUEUE_REFRESH_IN_SECONDS);

  const [tasks1, setTasks1] = useState<Array<Task>>([]);
  const [tasks1_old, setTasks1_old] = useState<Array<Task> | null>(null);
  const [audioTasks1, setAudioTask1] = useState<HTMLAudioElement | null>(null);

  const [tasks2, setTasks2] = useState<Array<Task>>([]);
  const [tasks2_old, setTasks2_old] = useState<Array<Task> | null>(null);
  const [audioTasks2, setAudioTask2] = useState<HTMLAudioElement | null>(null);

  const [tasks3, setTasks3] = useState<Array<Task>>([]);
  const [tasks3_old, setTasks3_old] = useState<Array<Task>>([]);
  const [audioTasks3, setAudioTask3] = useState<HTMLAudioElement | null>(null);

  const [tasks4, setTasks4] = useState<Array<Task>>([]);
  const [tasks4_old, setTasks4_old] = useState<Array<Task>>([]);
  const [audioTasks4, setAudioTask4] = useState<HTMLAudioElement | null>(null);

  const [assetId, setAssetId] = useState<number | null>(null);
  const [lastFetch, setLastFetch] = useState(moment());
  const [sessionTerminalMappingId] = useSessionStorage(STORAGE_KEYS.TERMINAL_MAPPING_ID, null);

  const { data: capCategories, isValidating: loadingCapCategories } = useSWR<CapacityCategory[]>(
    sessionTerminalMappingId
      ? `${API_URL.CAPACITY_CATEGORY_LIST_BY_TERMINAL_MAPPING}?assetClassId=${assetClass}&terminalMappingId=${sessionTerminalMappingId}`
      : `${API_URL.CAPACITY_CATEGORY_LIST}?parentId=${assetClass}&visibleOnly=true`,
    fetcher,
    {},
  );

  useEffect(() => {
    // Primárně načteme assetId z účtu uživatele
    if (user && user.assetId) {
      setAssetId(user.assetId);
      return;
    }

    // Uživatel assetId nemá, načteme assetId, které mohlo být nastaveno při přihlášení (např. přes URL)
    if (assetIdFromUrl) {
      setAssetId(assetIdFromUrl);
      return;
    }

    // Pokud existuje terminaMapping, načteme ID z něhoo - poslední možnost #430 - id je uložené v sessinStorage
    if (
      sessionTerminalMappingId &&
      terminalMappings &&
      terminalMappings.length > 0 &&
      terminalMappings[0]
    ) {
      const current = terminalMappings.find((x) => x.id === sessionTerminalMappingId);
      if (current) setAssetId(current.assignedAssetId);
      return;
    }

    // Při přihkášení assetId nepřišlo, podíváme se do URL přímo na této obrazovce
    const _assetIdFromUrl: number | null = queryParams.assetId ? Number(queryParams.assetId) : null;
    setAssetId(_assetIdFromUrl);
  }, []);

  useEffect(() => {
    if (capCategories) {
      fetchTasks();
    }
  }, [capCategories, assetId]);

  useEffect(() => {
    let shouldWait = false;
    const oldTaskIds_1 = tasks1_old
      ?.slice(0, 3)
      .map((t) => t.id)
      .sort((a, b) => a - b);
    const taskIds_1 = tasks1
      .slice(0, 3)
      .map((t) => t.id)
      .sort((a, b) => a - b);
    const isDiff_1 = taskIds_1.filter((x: any) => !oldTaskIds_1?.includes(x)).length > 0;

    if (isDiff_1) {
      setTasks1_old(tasks1);
      try {
        if (audioTasks1) audioTasks1.play();
        shouldWait = true;
      } catch {}
    }

    setTimeout(
      function () {
        const oldTaskIds_2 = tasks2_old
          ?.slice(0, 3)
          .map((t) => t.id)
          .sort((a, b) => a - b);
        const taskIds_2 = tasks2
          .slice(0, 3)
          .map((t) => t.id)
          .sort((a, b) => a - b);
        const isDiff_2 = taskIds_2.filter((x: any) => !oldTaskIds_2?.includes(x)).length > 0;

        if (isDiff_2) {
          setTasks2_old(tasks2);
          try {
            if (audioTasks2) audioTasks2.play();
            shouldWait = true;
          } catch {}
        } else {
          shouldWait = false;
        }

        setTimeout(
          function () {
            const oldTaskIds_3 = tasks3_old
              .slice(0, 3)
              .map((t) => t.id)
              .sort((a, b) => a - b);
            const taskIds_3 = tasks3
              .slice(0, 3)
              .map((t) => t.id)
              .sort((a, b) => a - b);
            const isDiff_3 = taskIds_3.filter((x: any) => !oldTaskIds_3?.includes(x)).length > 0;

            if (isDiff_3) {
              setTasks3_old(tasks3);
              try {
                if (audioTasks3) audioTasks3.play();
                shouldWait = true;
              } catch {}
            } else {
              shouldWait = false;
            }

            setTimeout(
              function () {
                const oldTaskIds_4 = tasks4_old
                  .slice(0, 3)
                  .map((t) => t.id)
                  .sort((a, b) => a - b);
                const taskIds_4 = tasks4
                  .slice(0, 3)
                  .map((t) => t.id)
                  .sort((a, b) => a - b);
                const isDiff_4 =
                  taskIds_4.filter((x: any) => !oldTaskIds_4?.includes(x)).length > 0;

                if (isDiff_4) {
                  setTasks4_old(tasks4);
                  try {
                    if (audioTasks4) audioTasks4.play();
                    shouldWait = true;
                  } catch {}
                } else {
                  shouldWait = false;
                }
              },
              shouldWait ? 1000 : 0,
            );
          },
          shouldWait ? 1000 : 0,
        );
      },
      shouldWait ? 1000 : 0,
    );
  }, [lastFetch]);

  useEffect(() => {
    const intervalId = setInterval(
      () => {
        fetchTasks();
      },
      refreshInterval && !Number.isNaN(Number(refreshInterval.value))
        ? Number(refreshInterval.value) * 1000
        : DEFAULT_REFRESH_INTERVAL,
    );
    return () => clearInterval(intervalId);
  }, [capCategories, assetId]);

  const isCCEnabled = (capacityCategoryIndex: number) => {
    return (
      capCategories &&
      (capacityCategoryIdArrFromUrl?.length === 0 ||
        capacityCategoryIdArrFromUrl?.find((x) => x === capCategories[capacityCategoryIndex].id))
    );
  };

  const fetchTasks = async () => {
    if (
      capCategories &&
      capCategories?.length > 0 &&
      capCategories[0] &&
      assetId &&
      isCCEnabled(0)
    ) {
      await getListApi<Array<Task>>({
        url: `${API_URL.TASK_LIST_QUEUE}?capacityCategoryId=${capCategories[0].id}&assetId=${assetId}`,
        params: {},
        callAfterSuccess: (result) => {
          setTasks1(result);
          setAudioTask1(
            new Audio(process.env.PUBLIC_URL + `/sounds/${capCategories[0].assistanceQueueSound}`),
            //new Audio(process.env.PUBLIC_URL + `/sounds/beep.mp3`),
          );
        },
      });
    }

    if (
      capCategories &&
      capCategories?.length > 1 &&
      capCategories[1] &&
      assetId &&
      isCCEnabled(1)
    ) {
      await getListApi<Array<Task>>({
        url: `${API_URL.TASK_LIST_QUEUE}?capacityCategoryId=${capCategories[1].id}&assetId=${assetId}`,
        params: {},
        callAfterSuccess: (result) => {
          setTasks2(result);
          setAudioTask2(
            new Audio(process.env.PUBLIC_URL + `/sounds/${capCategories[1].assistanceQueueSound}`),
            //new Audio(process.env.PUBLIC_URL + `/sounds/beep2.mp3`),
          );
        },
      });
    }

    if (
      capCategories &&
      capCategories?.length > 2 &&
      capCategories[2] &&
      assetId &&
      isCCEnabled(2)
    ) {
      await getListApi<Array<Task>>({
        url: `${API_URL.TASK_LIST_QUEUE}?capacityCategoryId=${capCategories[2].id}&assetId=${assetId}`,
        params: {},
        callAfterSuccess: (result) => {
          setTasks3(result);
          setAudioTask3(
            new Audio(process.env.PUBLIC_URL + `/sounds/${capCategories[2].assistanceQueueSound}`),
            //new Audio(process.env.PUBLIC_URL + `/sounds/beep3.mp3`),
          );
        },
      });
    }

    if (
      capCategories &&
      capCategories?.length > 3 &&
      capCategories[3] &&
      assetId &&
      isCCEnabled(3)
    ) {
      await getListApi<Array<Task>>({
        url: `${API_URL.TASK_LIST_QUEUE}?capacityCategoryId=${capCategories[3].id}&assetId=${assetId}`,
        params: {},
        callAfterSuccess: (result) => {
          setTasks4(result);
          setAudioTask4(
            new Audio(process.env.PUBLIC_URL + `/sounds/${capCategories[3].assistanceQueueSound}`),
            //new Audio(process.env.PUBLIC_URL + `/sounds/beep4.mp3`),
          );
        },
      });
    }

    setLastFetch(moment());
  };

  let tasks3Style = { borderRight: '1px solid white' };
  if (capCategories && capCategories.length <= 3) tasks3Style = { borderRight: 'none' };

  return (
    <PageContentWrapper
      noContentBlock
      noDxCard
      noResponsivePaddings
      loading={loadingCapCategories}
      error={!tasks1 || !tasks2 || !tasks3 || !tasks4}
    >
      {capCategories && (
        <Box>
          {tasks1 && capCategories.length > 0 && isCCEnabled(0) && (
            <Item ratio={1}>
              <AssistanceDataGrid
                category={capCategories[0]}
                data={tasks1}
                color={capCategories[0].colorName}
              />
            </Item>
          )}
          {tasks2 && capCategories.length > 1 && isCCEnabled(1) && (
            <Item ratio={1}>
              <div style={{ borderLeft: '1px solid white', borderRight: '1px solid white' }}>
                <AssistanceDataGrid
                  category={capCategories[1]}
                  data={tasks2}
                  color={capCategories[1].colorName}
                />
              </div>
            </Item>
          )}
          {tasks3 && capCategories.length > 2 && isCCEnabled(2) && (
            <Item ratio={1}>
              <div style={tasks3Style}>
                <AssistanceDataGrid
                  category={capCategories[2]}
                  data={tasks3}
                  color={capCategories[2].colorName}
                />
              </div>
            </Item>
          )}
          {tasks4 && capCategories.length > 3 && isCCEnabled(3) && (
            <Item ratio={1}>
              <AssistanceDataGrid
                category={capCategories[3]}
                data={tasks4}
                color={capCategories[3].colorName}
              />
            </Item>
          )}
        </Box>
      )}
    </PageContentWrapper>
  );
}

export default TerminalAssistanceQueue;
